import {
  ANALYTICS_EVENTS,
  INPUT_AUTOCOMPLETE,
  INPUT_TYPE,
  schemas,
  SIGN_UP_VERIFICATION_DISCLAIMER,
  colors,
} from '@nandosaus/constants';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Alert } from '../../alert';
import { analytics } from '../../../analytics';
import { Box } from '../../grid';
import { Form } from '../../form';
import { FormikTextInput } from '../../input';
import { navigation } from '../../../constants';
import { P, A } from '../../typography/new';
import { setLandingPage } from '../../../utils/auth';

const SignIn = ({
  error,
  setError,
  setMfa,
  signIn,
  showAlert,
  headingText,
  landingPage,
  hideCreateAccountLink,
  noXPadding,
  isCheckout,
}) => {
  const [signedIn, setSignedIn] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  return (
    <Form
      isCheckout={isCheckout}
      bottomElement={
        hideCreateAccountLink ? null : (
          <Box py="16px" mt="16px" textAlign="center">
            <P variant="standard-semi-bold">
              Don’t have an account?
              <A
                variant="standard text-link"
                href={navigation.CREATE_ACCOUNT}
                onClick={() => {
                  if (!landingPage) {
                    return;
                  }
                  analytics.track(ANALYTICS_EVENTS.USER_SIGN_UP_CLICKED, {
                    location: 'Sign In',
                  });
                  setLandingPage(landingPage);
                }}
                textDecoration="underline"
                ml="12px"
              >
                Create account
              </A>
            </P>
          </Box>
        )
      }
      bottomPadding={hideCreateAccountLink ? 1 : undefined}
      submitted={signedIn}
      initialValues={{ email: '', password: '' }}
      onSubmit={async ({ email, password }) => {
        document.activeElement.blur();

        try {
          analytics.track(ANALYTICS_EVENTS.USER_SIGN_IN_SUBMITTED);

          const token = await executeRecaptcha('sign_in');
          const { user } = await signIn({ email, password, token });
          // TODO: handle redirect for no mobile
          if (user.challengeName === 'SMS_MFA') {
            setError(null);
            setMfa({ user, email, password });
            analytics.track(ANALYTICS_EVENTS.USER_MULTI_FACTOR_AUTH_SUBMITTED);
          } else {
            setError(null);
            setSignedIn(true);
          }
        } catch (err) {
          analytics.track(ANALYTICS_EVENTS.USER_SIGN_IN_FAILED, { failureReason: err.message });
          setError(err.message);
        }
      }}
      buttonText="Sign in"
      error={error}
      heading={headingText}
      noXPadding={noXPadding}
      validationSchema={schemas.signIn}
      formBackgroundColor={colors.warmWhite2}
    >
      {showAlert && (
        <Box mb="16px">
          <Alert data-testid="account-verification-email-alert" type="info">
            {SIGN_UP_VERIFICATION_DISCLAIMER}
          </Alert>
        </Box>
      )}
      <Box mb="16px">
        <FormikTextInput
          name="email"
          label="Email address"
          type={INPUT_TYPE.EMAIL}
          autoComplete={INPUT_AUTOCOMPLETE.EMAIL}
        />
      </Box>
      <Box mb="8px">
        <FormikTextInput
          type={INPUT_TYPE.PASSWORD}
          name="password"
          label="Password"
          autoComplete={INPUT_AUTOCOMPLETE.PASSWORD}
        />
      </Box>
      <Box mt={1} textAlign="right" mb={2}>
        <A
          variant="small-semi-bold"
          href={navigation.FORGOT_PASSWORD}
          onClick={() => analytics.track(ANALYTICS_EVENTS.USER_RESET_PASSWORD_CLICKED)}
          textDecoration="underline"
        >
          Forgotten password?
        </A>
      </Box>
    </Form>
  );
};

SignIn.propTypes = {
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  setMfa: PropTypes.func.isRequired,
  signIn: PropTypes.func.isRequired,
  showAlert: PropTypes.bool,
  headingText: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  landingPage: PropTypes.string,
  hideCreateAccountLink: PropTypes.bool,
  noXPadding: PropTypes.bool,
  isCheckout: PropTypes.bool,
};

SignIn.defaultProps = {
  error: null,
  showAlert: false,
  headingText: 'Sign in to your account',
  landingPage: null,
  hideCreateAccountLink: false,
  noXPadding: false,
  isCheckout: false,
};

export { SignIn };
